import { useEffect } from "react";
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from "../utils/axiosInstance";
import InputFieldAddress from "./InputFieldAddress";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
    Button, TextField, Stack, FormControlLabel, Switch
} from "@mui/material";

export default function SupplierModal({ open, onClose, onSaved, onDeleted, supplier }) {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const isEdit = Boolean(supplier);

    const { register, handleSubmit, reset, setValue, control, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            supplier_code: "",
            supplier_name: "",
            supplier_phone: "",
            //email: "",
            //phone: "",
            postal_code: "",
            state: "",
            address_line: "",
            closing_day: "",
            payment_day: "",
            status: "active" // デフォルトは active
        }
    });

    // 編集時データセット
    useEffect(() => {
        if (supplier) {
            reset({
                supplier_code: supplier.supplier_code || "",
                supplier_name: supplier.supplier_name || "",
                supplier_phone: supplier.supplier_phone || "",
                //email: supplier.email || "",
                //phone: supplier.phone || "",
                postal_code: supplier.postal_code || "",
                state: supplier.state || "",
                address_line: supplier.address_line || "",
                closing_day: supplier.closing_day?.toString() || "",
                payment_day: supplier.payment_day?.toString() || "",
                status: supplier.status || "active",
            });
        } else {
            reset();
        }
    }, [supplier, open, reset]);

    // submit
    const onSubmit = async (data) => {
        try {
            // 数値変換と不要フィールド削除
            const payload = {
                ...data,
                closing_day: data.closing_day ? Number(data.closing_day) : null,
                payment_day: data.payment_day ? Number(data.payment_day) : null,
            };

            let res;
            if (isEdit) {
                res = await axiosInstance.put(`/supplier/${supplier.supplier_id}`, payload);
            } else {
                res = await axiosInstance.post("/supplier", payload);
            }

            onSaved(res.data);
            reset();
            onClose();

        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || "失敗しました", 'error');
        }
    };

    // 削除（無効化）
    const handleDelete = async () => {
        if (!supplier) return;
        if (!window.confirm("この仕入先を削除しますか？")) return;

        try {
            const payload = {
                ...supplier,
                status: "invalid"
            };

            const res = await axiosInstance.put(`/supplier/${supplier.supplier_id}`, payload);
            onDeleted && onDeleted(supplier.supplier_id);
            showSnackbar(res.data?.message || '削除しました', 'success');
            navigate('/suppliers');
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || "削除に失敗しました", 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? "仕入れ先編集" : "仕入れ先追加"}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} mt={1}>
                    <TextField
                        label="仕入先コード"
                        {...register("supplier_code", {
                            maxLength: { value: 20, message: "20文字以内で入力してください" }
                        })}
                        error={!!errors.supplier_code}
                        helperText={errors.supplier_code?.message}
                        inputProps={{ maxLength: 20 }}
                    />
                    <TextField
                        label="仕入先企業名"
                        {...register("supplier_name", {
                            required: "仕入先企業名を入力してください",
                            maxLength: { value: 255, message: "255文字以内で入力してください" }
                        })}
                        error={!!errors.supplier_name}
                        helperText={errors.supplier_name?.message}
                        inputProps={{ maxLength: 255 }}
                    />
                    <InputFieldAddress
                        label='住所'
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        control={control}
                        isEditMode={true}
                    />
                    <TextField
                        label="電話番号"
                        {...register("supplier_phone", {
                            maxLength: {
                                value: 20,
                                message: "20文字以内で入力してください"
                            },
                            pattern: {
                                value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                                message: "電話番号形式が正しくありません"
                            }
                        })}
                        error={!!errors.supplier_phone}
                        helperText={errors.supplier_phone?.message}
                        inputProps={{ maxLength: 20 }}
                    />
                    <Stack direction='row' spacing={1}>
                        <TextField
                            label="締め日"
                            type="number"
                            {...register("closing_day", {
                                min: { value: 1, message: "1〜31で入力してください" },
                                max: { value: 31, message: "1〜31で入力してください" }
                            })}
                            error={!!errors.closing_day}
                            helperText={errors.closing_day?.message}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">日</InputAdornment>
                            }}
                            sx={{ width: 150 }}
                        />
                        <TextField
                            label="支払日"
                            type="number"
                            {...register("payment_day", {
                                min: { value: 1, message: "1〜31で入力してください" },
                                max: { value: 31, message: "1〜31で入力してください" }
                            })}
                            error={!!errors.payment_day}
                            helperText={errors.payment_day?.message}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">日</InputAdornment>
                            }}
                            sx={{ width: 150 }}
                        />
                    </Stack>
                    {isEdit && (
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={field.value === "active"}
                                            onChange={(e) =>
                                                field.onChange(e.target.checked ? "active" : "pending")
                                            }
                                        />
                                    }
                                    label="有効"
                                />
                            )}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                {supplier && (
                    <Button
                        color="error" variant="outlined"
                        onClick={handleDelete}
                        sx={{ mr: 'auto' }}
                    >
                        削除する
                    </Button>
                )}

                <Button onClick={onClose} variant="outlined">キャンセル</Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (isEdit ? "更新中..." : "作成中...") : (isEdit ? "更新" : "作成")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}